import { useEffect, useRef, useState } from 'react';

import { api } from '../../services/api';
import type { GamificationEvent } from '../../types/auth';
import { celebrationCopy } from './celebrationCopy';

/**
 * CORE-509 — her line for this celebration: generated when it arrives in time,
 * the static fallback otherwise.
 *
 * ## Why it waits, briefly
 *
 * The alternative — show the fallback immediately and swap in the generated
 * line when it lands — rewrites a sentence under the reader's eyes, which is
 * worse than a short pause. She is a chat surface, so a pause is legible: the
 * panel shows her typing. `MAX_WAIT_MS` caps it, because a celebration that
 * hangs on a model call has stopped being a celebration.
 *
 * ## Why failure is silent
 *
 * Every failure — off, slow, refused by the wording rules, network — resolves
 * to `celebrationCopy(event)`, which is always safe and never states a right.
 * The generated half can be absent without anyone noticing; it must never be
 * the reason the popup is empty.
 */
const MAX_WAIT_MS = 4_000;

/**
 * The request half, pure and injectable: everything that decides whether there
 * IS a generated line, with no React around it. `null` always means "use the
 * fallback" — an empty body, a refusal, or a failed call are the same answer as
 * far as the popup is concerned.
 */
export async function fetchCelebrationLine(
  eventId: number,
  fetcher: (id: number) => Promise<{ text: string | null }> = (id) => api.getCelebrationLine(id),
): Promise<string | null> {
  try {
    const res = await fetcher(eventId);
    const text = (res?.text || '').trim();
    return text || null;
  } catch {
    return null;
  }
}

export type CelebrationLine = {
  text: string;
  /** True while her line is still being written — render a typing indicator. */
  pending: boolean;
  /** True when the text came from the model rather than the fallback. */
  generated: boolean;
};

export function useCelebrationLine(event: GamificationEvent | null): CelebrationLine {
  const [line, setLine] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  // Which event the current answer belongs to, so a slow reply for a dismissed
  // event can never be painted over the next one.
  const forEventId = useRef<number | null>(null);

  useEffect(() => {
    if (!event) {
      setLine(null);
      setPending(false);
      forEventId.current = null;
      return;
    }
    let alive = true;
    const id = event.id;
    forEventId.current = id;
    setLine(null);
    setPending(true);

    const stopWaiting = setTimeout(() => {
      if (alive && forEventId.current === id) setPending(false);
    }, MAX_WAIT_MS);

    fetchCelebrationLine(id).then((text) => {
      if (!alive || forEventId.current !== id) return;
      if (text) setLine(text);
      setPending(false);
    });

    return () => {
      alive = false;
      clearTimeout(stopWaiting);
    };
  }, [event]);

  const fallback = event ? celebrationCopy(event).text : '';
  return {
    text: line ?? fallback,
    pending: pending && line == null,
    generated: line != null,
  };
}
